/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// React
import { useState } from 'react';

// Mantine
import {
	Popover,
	Checkbox,
	Box,
	Stack,
	Button,
	Image,
	ActionIcon,
	Group,
	Text,
} from '@mantine/core';

import {
	useClickOutside,
	useMediaQuery,
} from '@mantine/hooks';

// Styles
import classes from './MultiSelect.module.css';
import clsx from 'clsx';

// Icons
import AddOutlineIcon from '@assets/icons/add-outline.svg';
import AddOutlineDisabledIcon from '@assets/icons/add-outline-disabled.svg';

// Utils
import { truncateString } from '@utils/functions';

// Types
import { TSelectOption } from '@global-types/index';

// I18n
import { useTranslation } from 'react-i18next';

// Components
import { Tooltip } from '@components/atoms';

/* -------------------------------------------------------------------------- */
/*                                     Type                                   */
/* -------------------------------------------------------------------------- */
type MultiSelectFieldProps = {
	initialValue?: TSelectOption[] | [];
	options: TSelectOption[];
	value: TSelectOption[];
	disabled?: boolean;
	optionType: string;
	handleapply: (item: TSelectOption[]) => void;
	opened?: boolean;
};

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */
export const MultiSelect = (
	props: MultiSelectFieldProps
) => {
	/* ******************************** PROPS ******************************* */
	const {
		initialValue,
		options,
		value,
		disabled,
		optionType,
		handleapply,
		...restProps
	} = props;
	/* ******************************** States ******************************* */
	const [activeFields, setActiveFields] = useState<
		TSelectOption[] | []
	>(initialValue ? initialValue : []);

	/* ******************************** HELPERS ******************************* */
	const handleRemoveSelectedOptions = () => {
		setActiveFields(value);
	};

	const handleCheckboxChange = (item: TSelectOption) => {
		const isSelected = activeFields.some(
			(selectedTSelectOption: TSelectOption) =>
				selectedTSelectOption.value === item.value
		);

		if (isSelected) {
			setActiveFields(
				activeFields.filter(
					(selectedTSelectOption: TSelectOption) =>
						selectedTSelectOption.value !== item.value
				)
			);
		} else {
			setActiveFields([...activeFields, item]);
		}
	};

	const handleSelectAll = () => {
		setActiveFields(options);
	};

	const handleApply = () => {
		handleapply(activeFields);
	};

	/* ******************************** I18N ******************************* */
	const { t } = useTranslation();

	/* ******************************** REF ******************************* */
	const refDropdown = useClickOutside(
		handleRemoveSelectedOptions
	);

	/* ******************************** Media Query ******************************* */
	const isBigScreen = useMediaQuery('(min-width: 120em)');

	/* ******************************** RENDERING ******************************* */
	return (
		<Popover
			data-testid="M00G199_Z5jdHi6Q-component"
			position="bottom"
			classNames={classes}
			clickOutsideEvents={['mouseup', 'touchend']}
			disabled={disabled}
			offset={-100}
			{...restProps}
		>
			<Popover.Target>
				<Box
					className={clsx(
						classes.container,
						disabled && classes.disabled
					)}
				>
					<ActionIcon size={'17px'} variant="transparent">
						<Image
							src={
								disabled
									? AddOutlineDisabledIcon
									: AddOutlineIcon
							}
						/>
					</ActionIcon>
					<Text
						className={clsx(
							classes.text,
							disabled && classes.textDisabled
						)}
					>
						{`${t(
							'global.add'
						)} ${optionType.toLowerCase()}`}
					</Text>
				</Box>
			</Popover.Target>

			<Popover.Dropdown
				className={classes.containerDropdown}
				ref={refDropdown}
			>
				<Box className={classes.containerOptions}>
					<Stack
						gap={'md'}
						style={{
							padding: '0px 5px',
						}}
					>
						<Group justify="space-between">
							<Text className={classes.headerTitle}>
								{`${t(
									'global.displayed'
								)} ${optionType.toLowerCase()}`}
							</Text>
							<Text
								data-testid="M00G199_Z5jsai6Q-button"
								onClick={handleSelectAll}
								className={classes.selectAll}
							>
								{t('global.selectAll')}
							</Text>
						</Group>
						{props &&
						options &&
						Array.isArray(options) &&
						options.length > 0 ? (
							options.map((item: TSelectOption) => (
								<Tooltip
									key={item.value}
									label={item.label}
									isActive={item.label.length > 24}
								>
									<Box>
										<Checkbox
											labelPosition="left"
											data-testid={item.label}
											checked={
												activeFields &&
												Array.isArray(activeFields)
													? activeFields.some(
															(selectedTSelectOption) =>
																selectedTSelectOption.value ===
																item.value
														)
													: false
											}
											onChange={() => {
												handleCheckboxChange(item);
											}}
											size={isBigScreen ? 'sm' : 'xs'}
											classNames={{
												label: classes.textOption,
												labelWrapper:
													classes.labelWrapper,
											}}
											label={truncateString(
												t(item.label),
												24
											)}
										/>
									</Box>
								</Tooltip>
							))
						) : (
							<Text
								className={
									classes.emptyTSelectOptionsText
								}
							>
								{t('global.noData')}
							</Text>
						)}
					</Stack>
				</Box>
				<Box className={classes.containerButton}>
					<Button
						onClick={handleApply}
						size={isBigScreen ? 'sm' : 'xs'}
						fullWidth
						data-testid="M00G199_Z5jdHi6Q-button"
					>
						{t('global.apply')}
					</Button>
				</Box>
			</Popover.Dropdown>
		</Popover>
	);
};
